import React, { useState } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';

import { Account, GUID, RMDCalculationResult } from '@ndvr/portfolio-api';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from 'utils/number-formatter';

import { useRmdTransactions } from './rmd-queries';

interface RmdTransactionHistoryProps {
    accountGUIDs: GUID<Account>[];
    accounts: RMDCalculationResult[];
    taxYear: number;
    defaultOpen?: boolean;
}

export const RmdTransactionHistory: React.FC<RmdTransactionHistoryProps> = ({
    accountGUIDs,
    accounts,
    taxYear,
    defaultOpen = false,
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const { data, isLoading } = useRmdTransactions(accountGUIDs, taxYear, true);
    const transactions = data?.transactions ?? [];
    const totalNr = data?.totalNr ?? 0;
    const isTruncated = totalNr > transactions.length;

    const total = transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const accountByGUID = new Map(accounts.map((a) => [a.accountGUID, a]));

    return (
        <div className="rounded-md border bg-white">
            <button
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
            >
                <span className="flex items-center gap-2">
                    {open ? <LuChevronDown className="h-4 w-4" /> : <LuChevronRight className="h-4 w-4" />}
                    Transaction History
                </span>
                <span className="text-xs font-normal text-gray-500">
                    {isLoading
                        ? 'Loading…'
                        : `${transactions.length} transaction${transactions.length !== 1 ? 's' : ''}, ${formatCurrency(total)}`}
                </span>
            </button>
            {open && (
                <div className="max-h-60 overflow-y-auto">
                    <Table>
                        <TableHeader className="sticky top-0 z-10">
                            <TableRow className="bg-gray-50">
                                <TableHead className="text-xs font-medium text-gray-500">Date</TableHead>
                                <TableHead className="text-xs font-medium text-gray-500">Account</TableHead>
                                <TableHead className="text-xs font-medium text-gray-500">Description</TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="py-4 text-center text-sm text-gray-400">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="py-4 text-center text-sm text-gray-400">
                                        No transactions found for {taxYear}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {transactions.map((t) => (
                                        <TableRow key={t.transactionId}>
                                            <TableCell className="text-sm">{t.tradeDate}</TableCell>
                                            <TableCell className="text-sm text-gray-600">
                                                {t.portfolioGuid
                                                    ? (accountByGUID.get(t.portfolioGuid)?.accountName ?? t.pId ?? '—')
                                                    : (t.pId ?? '—')}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600">
                                                {t.transactionDetail ?? t.type}
                                            </TableCell>
                                            <TableCell className="text-right text-sm">
                                                {formatCurrency(Math.abs(t.amount))}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {isTruncated && (
                                        <TableRow>
                                            <TableCell colSpan={4} className="py-2 text-center text-xs text-amber-600">
                                                Showing {transactions.length} of {totalNr} transactions
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow className="border-t-2 bg-gray-50 font-semibold">
                                        <TableCell colSpan={3} className="text-sm">
                                            {isTruncated ? `Total (first ${transactions.length})` : 'Total'}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">{formatCurrency(total)}</TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};
